// ============================================================
//  ANNIHILATION — THE CODEX
//  The four Ilk, worn open. Everything the Emperium left behind.
//
//  ONE SOURCE OF TRUTH: this screen READS from beings.js and
//  BeingSprite.js. It authors NO lore of its own — every name,
//  epigraph, ability, and effect string is pulled live from the
//  registry, so a retune in beings.js shows up here for free and
//  the Codex can never drift from the game (the guide-vs-code
//  failure the codebase already knows by name).
//
//  Vyren pattern: being ids drive the sprite + palette; the
//  lineage / lordName / ability names are display strings shown
//  verbatim from canon.
//
//  Navigable through the main menu (Plaza StartMenu → Codex),
//  mounted as a full-screen overlay exactly like Deeds & Renown.
// ============================================================

import { useState, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { PlayerBody } from "./fold3dKit";
import {
  GM, OBSIDIAN_BG, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  OBSIDIAN_PANEL, GOLD_METAL_H, facetTB,
  SHADOW_RAISED, SHADOW_RECESS, SHADOW_FLOAT, textGlow, TEXT_SET,
} from "./theme";
import { StarDust, NoiseOverlay, UnstableWorldBG } from "./TerrorUI";
import { useModalKeys } from "./KeyboardNav";
import { BeingSprite, getBeingPalette } from "./BeingSprite";
import { BEINGS, BEING_IDS, SKILL_SLOTS } from "./beings";
import { TIERS } from "./stats";
import { slotRequirementLabel } from "./PlayerController";

// ── The slot badge ────────────────────────────────────────────
// ⚑ BATCH GATE-1 (Jul 25 2026): this used to be the "first
// available" column, rendering SKILL_SLOTS[].firstAvailable as a
// tier name. That field is DELETED and tier gating with it — the
// Codex was describing a system that no longer exists.
//
// What replaces it is better, and truer to this file's own law:
// the badge now prints what the slot ACTUALLY costs you, read
// live from source in priority order —
//
//   1. its AENERGY THRESHOLD, if it has one ("125 Chao"), from
//      PlayerController's CAST table via slotRequirementLabel;
//   2. otherwise "rest-gated" — the real meta-rhythm gate, which
//      is a heavier restriction than any tier ever was;
//   3. otherwise its cadence ("2–4s"), straight off SKILL_SLOTS;
//   4. otherwise, for the Lyt, that it is paced by the pool
//      rather than by a timer.
//
// Nothing is authored here. Every string is derived, so a retune
// anywhere shows up in the Codex for free and the guide-vs-code
// drift this file exists to prevent stays prevented.
const slotBadge = (slot) => {
  if (!slot) return "";
  const req = slotRequirementLabel(slot.id);
  if (req) return req;
  if (slot.restGated) return "rest-gated";
  if (Array.isArray(slot.cooldownSec)) return `${slot.cooldownSec[0]}–${slot.cooldownSec[1]}s`;
  if (slot.cooldown === "none") return "Lyt-paced";
  return "";
};

// ── Section header ────────────────────────────────────────────
function SectionLabel({ children, color }) {
  return (
    <div style={{
      fontSize: 9, letterSpacing: 3, fontFamily: GM.font,
      color: color || GM.vellumFaint, textTransform: "uppercase",
      marginBottom: 10, textShadow: TEXT_SET,
    }}>{children}</div>
  );
}

// ── One ability row — name + effect, tinted by the being ──────
function AbilityRow({ slot, name, effect, avail, color }) {
  return (
    <div style={{
      padding: "10px 12px", borderRadius: 8,
      background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`,
      boxShadow: SHADOW_RAISED, marginBottom: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 2,
            fontFamily: GM.font }}>{slot}</span>
          <span style={{ fontSize: 12, fontWeight: 900, color,
            fontFamily: GM.font, textShadow: textGlow(color) }}>{name}</span>
        </div>
        {avail && (
          <span style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 1,
            fontFamily: GM.font, whiteSpace: "nowrap" }}>◆ {avail}</span>
        )}
      </div>
      {effect && (
        <div style={{ fontSize: 10.5, lineHeight: 1.5, color: GM.vellumDim,
          fontFamily: GM.font }}>{effect}</div>
      )}
    </div>
  );
}

// ── The portrait rail — one selectable being per tile ─────────
function BeingTile({ id, active, onPick }) {
  const b = BEINGS[id];
  const pal = getBeingPalette(id);
  return (
    <button
      onClick={() => onPick(id)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "8px 10px", borderRadius: 10,
        cursor: "pointer", textAlign: "left",
        background: active ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
        border: `1px solid ${active ? pal.main : GM.pageBorder}`,
        boxShadow: active ? `0 0 14px ${pal.glow}44, ${SHADOW_RAISED}` : SHADOW_RAISED,
        transition: "all 0.18s",
      }}>
      <div style={{
        width: 44, height: 44, flexShrink: 0, borderRadius: 8,
        background: `radial-gradient(circle at 50% 40%, ${pal.deep}, #050505)`,
        border: `1px solid ${pal.main}55`, overflow: "hidden",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}>
        <div style={{ width: "88%", height: "88%" }}>
          <BeingSprite beingId={id} isIdle facing={{ x: 0, y: 1 }} />
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 900, fontFamily: GM.font,
          color: active ? pal.glow : GM.vellum,
          textShadow: active ? textGlow(pal.glow) : TEXT_SET,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {b.lineage}
        </div>
        <div style={{ fontSize: 8.5, color: GM.vellumFaint, letterSpacing: 1,
          fontFamily: GM.font }}>{b.ilk} · {b.archetype}</div>
      </div>
    </button>
  );
}

// ── The 3D hero stage — the living portrait (Batch 3D-8) ──────
// The SAME PlayerBody the game renders, on a slow turntable, with
// the Ascent toggle beneath it: Beb → Champion → Lord. Tier here
// is display-only (the Codex writes nothing); the bodies swap in
// fold3dKit exactly as they will in the field at L30 / L60.
// No OrbitControls — a wheel-zoom canvas inside a scrolling modal
// hijacks the page scroll; the turntable does the showing.
const STAGE_TIERS = [
  { id: TIERS.BEB, label: "Beb" },
  { id: TIERS.CHAMPION, label: "Champion" },
  { id: TIERS.LORD, label: "Lord" },
];

function StageTurn({ children }) {
  const grp = useRef();
  useFrame((_, dt) => { if (grp.current) grp.current.rotation.y += dt * 0.35; });
  return <group ref={grp}>{children}</group>;
}

function HeroStage3D({ beingId }) {
  const pal = getBeingPalette(beingId);
  const [tier, setTier] = useState(TIERS.BEB);

  return (
    <div style={{ width: 240, flexShrink: 0 }}>
      <div style={{
        width: 240, height: 250, borderRadius: 14, overflow: "hidden",
        background: `radial-gradient(circle at 50% 38%, ${pal.deep}, #050505)`,
        border: `1px solid ${pal.main}`, boxShadow: `0 0 22px ${pal.glow}33, ${SHADOW_FLOAT}`,
      }}>
        <Canvas shadows camera={{ position: [0, 1.6, 4.2], fov: 38 }} dpr={[1, 1.5]}>
          <fog attach="fog" args={["#0b0a10", 7, 16]} />
          {/* floor + pedestal ring */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
            <circleGeometry args={[3.4, 36]} />
            <meshStandardMaterial color="#141019" roughness={1} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.004, 0]}>
            <ringGeometry args={[1.06, 1.16, 36]} />
            <meshBasicMaterial color={pal.glow} transparent opacity={0.55} toneMapped={false} />
          </mesh>
          {/* lights: warm key, palette rim, soft fill */}
          <ambientLight intensity={0.45} />
          <hemisphereLight args={["#2a2436", "#0b0a10", 0.5]} />
          <directionalLight position={[4, 6, 5]} intensity={0.9} castShadow
            shadow-mapSize-width={512} shadow-mapSize-height={512} />
          <pointLight position={[-3, 2, -2.2]} color={pal.glow} intensity={1.2} distance={9} />
          <StageTurn>
            <PlayerBody beingId={beingId} tier={tier} facing={{ x: 0, y: 1 }} moving={false} />
          </StageTurn>
        </Canvas>
      </div>
      {/* the Ascent toggle */}
      <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
        {STAGE_TIERS.map((t) => {
          const on = tier === t.id;
          return (
            <button key={t.id} onClick={() => setTier(t.id)} style={{
              flex: 1, padding: "5px 0", borderRadius: 6, cursor: "pointer",
              fontFamily: GM.font, fontSize: 9.5, fontWeight: 900, letterSpacing: 1,
              color: on ? "#0b0a10" : GM.vellumDim,
              background: on ? pal.glow : OBSIDIAN_SLAB,
              border: `1px solid ${on ? pal.glow : GM.pageBorder}`,
              boxShadow: on ? `0 0 10px ${pal.glow}66` : SHADOW_RAISED,
              transition: "all 0.15s",
            }}>{t.label}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── The detail panel for the selected being ───────────────────
function BeingDetail({ id }) {
  const b = BEINGS[id];
  const pal = getBeingPalette(id);
  // canon may carry more than one line; the launch four each have one,
  // but read them all so a second line shows up automatically.
  const lines = b.lines || [];

  return (
    <div>
      {/* Hero — the living portrait + identity */}
      <div style={{ display: "flex", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <HeroStage3D key={id} beingId={id} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 22, fontWeight: 900, fontFamily: GM.font,
            color: pal.glow, textShadow: textGlow(pal.glow), lineHeight: 1.1 }}>
            {b.lineage}
          </div>
          <div style={{ fontSize: 11, color: GM.inkGold, letterSpacing: 1,
            fontFamily: GM.font, marginTop: 3, marginBottom: 8 }}>
            The Lord: {b.lordName}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {[b.ilk, b.beingClass, b.archetype].map((t, i) => (
              <span key={i} style={{
                fontSize: 8.5, letterSpacing: 1, fontFamily: GM.font,
                color: GM.vellumDim, padding: "3px 9px", borderRadius: 3,
                background: `${pal.main}18`, border: `1px solid ${pal.main}44`,
              }}>{t}</span>
            ))}
          </div>
          <div style={{ fontSize: 12, fontStyle: "italic", color: GM.vellum,
            fontFamily: GM.font, lineHeight: 1.5 }}>
            “{b.epigraph}”
          </div>
          <div style={{ fontSize: 11, color: pal.main, fontFamily: GM.font,
            marginTop: 8, fontWeight: 700 }}>{b.oneLiner}</div>
        </div>
      </div>

      {/* THE SEVEN-SLOT BAR — canon ability ladder, in slot order */}
      <SectionLabel color={pal.main}>The Seven-Slot Bar</SectionLabel>

      {/* Slot 1 — Lyt Attack */}
      {b.lytAttack && (
        <AbilityRow slot="◆ 1 · LYT"
          name={b.lytAttack.name} effect={b.lytAttack.effect}
          avail={slotBadge(SKILL_SLOTS[0])} color={pal.main} />
      )}

      {/* Slots 2–4 — Primera / Impera / U'hlti, per line */}
      {lines.map((line) => (
        <div key={line.id} style={{ marginBottom: 4 }}>
          {lines.length > 1 && (
            <div style={{ fontSize: 9.5, color: pal.glow, fontFamily: GM.font,
              letterSpacing: 1, margin: "8px 0 6px", fontStyle: "italic" }}>
              {line.name}
            </div>
          )}
          {line.primera && (
            <AbilityRow slot="◆ 2 · PRIMERA"
              name={line.primera.name} effect={line.primera.effect}
              avail={slotBadge(SKILL_SLOTS[1])} color={pal.main} />
          )}
          {line.impera && (
            <AbilityRow slot="◆ 3 · IMPERA"
              name={line.impera.name} effect={line.impera.effect}
              avail={slotBadge(SKILL_SLOTS[2])} color={pal.main} />
          )}
          {line.uhlti && (
            <AbilityRow slot="◆ 4 · U'HLTI"
              name={line.uhlti.name} effect={line.uhlti.effect}
              avail={slotBadge(SKILL_SLOTS[3])} color={pal.main} />
          )}
        </div>
      ))}

      {/* Slot 5 — Transformation */}
      {b.transformation && (
        <AbilityRow slot="◆ 5 · TRANSFORMATION"
          name={b.transformation.name}
          effect={b.transformation.counterplay
            ? `${b.transformation.effect}  ⚠ ${b.transformation.counterplay}`
            : b.transformation.effect}
          avail={slotBadge(SKILL_SLOTS[4])} color={pal.main} />
      )}

      {/* Slot 6 — King's Grace */}
      {b.kingsGrace && (
        <AbilityRow slot="◆ 6 · KING'S GRACE"
          name={b.kingsGrace.name} effect={b.kingsGrace.effect}
          avail={slotBadge(SKILL_SLOTS[5])} color={pal.main} />
      )}

      {/* Slot 7 — Emperium */}
      {b.emperium && (
        <AbilityRow slot="◆ 7 · EMPERIUM"
          name={b.emperium.name} effect={b.emperium.effect}
          avail={slotBadge(SKILL_SLOTS[6])} color={pal.main} />
      )}
    </div>
  );
}

// ── THE CODEX ─────────────────────────────────────────────────
export default function AnnihilationCodex({ onClose }) {
  useModalKeys(onClose);
  const [selected, setSelected] = useState(BEING_IDS[0]);
  const ids = useMemo(() => BEING_IDS, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: OBSIDIAN_BG, overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      <UnstableWorldBG />
      <StarDust />
      <NoiseOverlay />

      {/* Header rail */}
      <div style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px", borderBottom: `1px solid ${GM.pageBorder}`,
        background: GOLD_METAL_H, boxShadow: SHADOW_RAISED,
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, fontFamily: GM.font,
            color: GM.inkGoldBright, letterSpacing: 2, textShadow: textGlow(GM.inkGoldBright) }}>
            ◆ THE CODEX
          </div>
          <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2,
            fontFamily: GM.font, marginTop: 2 }}>
            The Four Ilk · everything the Emperium left behind
          </div>
        </div>
        <button onClick={onClose} style={{
          padding: "8px 16px", borderRadius: 6, cursor: "pointer",
          background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`,
          color: GM.vellumDim, fontFamily: GM.font, fontSize: 12,
          boxShadow: SHADOW_RAISED,
        }}>← Leave</button>
      </div>

      {/* Body — rail + detail */}
      <div style={{
        position: "relative", zIndex: 2, flex: 1, minHeight: 0,
        display: "flex", gap: 14, padding: 16,
      }}>
        {/* Rail */}
        <div style={{
          width: 208, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8,
          overflowY: "auto",
        }}>
          <SectionLabel>The Ilk</SectionLabel>
          {ids.map((id) => (
            <BeingTile key={id} id={id}
              active={id === selected} onPick={setSelected} />
          ))}
          <div style={{ marginTop: "auto", paddingTop: 12 }}>
            <div style={{ fontSize: 8, color: GM.vellumFaint, fontStyle: "italic",
              lineHeight: 1.5, fontFamily: GM.font }}>
              Every being in Terror TCG is a card because it lost.
              This is how they lived before they lost.
            </div>
          </div>
        </div>

        {/* Detail */}
        <div style={{
          flex: 1, minWidth: 0, overflowY: "auto",
          padding: "18px 20px", borderRadius: 14,
          background: OBSIDIAN_PANEL, border: `1px solid ${GM.pageBorder}`,
          boxShadow: SHADOW_RECESS,
        }}>
          <BeingDetail id={selected} />
        </div>
      </div>
    </div>
  );
}
